import { useEffect, useState } from 'react';
import { supabase } from '../supabase';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Read the current user's role from the `users` table.
 * Used by route guards (`ParentOnlyRoute`, `AdminOnlyRoute` future).
 *
 * Returns:
 *   - { loading: true } while fetching
 *   - { role: 'PARENT' | 'COACH' | 'PLAYER' | 'CLUB_ADMIN' | 'WELFARE_OFFICER' | 'SUPER_ADMIN' | null }
 *   - error if the row can't be read (RLS / network)
 *
 * Cached in component state — reload page to refresh. The role rarely
 * changes during a session.
 */

export type UserRole =
  | 'SUPER_ADMIN'
  | 'CLUB_ADMIN'
  | 'WELFARE_OFFICER'
  | 'COACH'
  | 'PARENT'
  | 'PLAYER';

interface State {
  role: UserRole | null;
  loading: boolean;
  error: string | null;
}

export function useUserRole(): State {
  const { user, bypass } = useAuth();
  const [state, setState] = useState<State>({ role: null, loading: true, error: null });

  useEffect(() => {
    let cancelled = false;
    if (!user) {
      setState({ role: null, loading: false, error: null });
      return;
    }
    // Bypass mode (dev-only): treat as COACH. Note: this is purely a
    // client-side convenience for dev iteration — admin surfaces still
    // gate on the real JWT via is_admin() RLS, which has no signed JWT
    // under bypass. To test admin pages you must turn off
    // VITE_AUTH_BYPASS and sign in for real.
    if (bypass) {
      setState({ role: 'COACH', loading: false, error: null });
      return;
    }
    setState((s) => ({ ...s, loading: true }));
    (async () => {
      const { data, error } = await supabase
        .from('users')
        .select('role')
        .eq('id', user.id)
        .single();
      if (cancelled) return;
      if (error) {
        setState({ role: null, loading: false, error: error.message });
        return;
      }
      setState({ role: (data?.role as UserRole) ?? null, loading: false, error: null });
    })();
    return () => {
      cancelled = true;
    };
  }, [user, bypass]);

  return state;
}
